/**
 * AvatarCropModal — CORE-326.
 *
 * Lets the user choose which part of a picked photo lands in their round
 * avatar instead of the backend / CSS center-cropping it blindly. The user
 * pans the image inside a circular viewport, zooms in/out, sees the round
 * preview live, then confirms — at which point the chosen region is rendered
 * to a canvas and handed back as a ready-cropped JPEG Blob (the existing
 * presigned-S3 upload path accepts a Blob as-is, so no API change is needed).
 *
 * Thin wrapper over the shared {@link ImageCropModal} (square + circular
 * viewport, 512² output). The crop engine itself is shared with the manifesto
 * poster crop (CORE-327). Web only — see ImageCropModal for the native note.
 * The testIDs (`avatar-crop-*`) and labels are kept verbatim so the CORE-326
 * e2e keeps passing against this wrapper.
 */
import React from 'react';
import { ImageCropModal } from './ImageCropModal';

/** Side of the (square) circular viewport in px. Circle is inscribed. */
const VIEWPORT = 260;
/** Exported avatar edge in px — square, masked to a circle on display. */
const OUTPUT = 512;

export interface AvatarCropModalProps {
  /** Picked image URI (data: / blob: from expo-image-picker). Null = closed. */
  imageUri: string | null;
  onCancel: () => void;
  /** Receives the cropped JPEG and a fresh object-URL for the preview. */
  onConfirm: (blob: Blob, previewUri: string) => void;
}

export const AvatarCropModal: React.FC<AvatarCropModalProps> = ({ imageUri, onCancel, onConfirm }) => (
  <ImageCropModal
    imageUri={imageUri}
    onCancel={onCancel}
    onConfirm={onConfirm}
    viewportW={VIEWPORT}
    viewportH={VIEWPORT}
    outputW={OUTPUT}
    outputH={OUTPUT}
    shape="circle"
    title="Кадрирование фото"
    hint="Перетащите фото и выберите масштаб"
    testIDPrefix="avatar-crop"
  />
);
